/**
 * salesRegisterDataSource.ts — WatermelonDB → RegisterInvoiceInput bridge.
 *
 * SALE + CREDIT_NOTE invoices raised in the range (voided excluded), with the
 * customer's name and GSTIN. The GST split is read straight off the invoice
 * header, so the register matches the invoices exactly.
 */

import { Q } from '@nozbe/watermelondb';
import { database } from '@/db';
import { fetchInChunks } from '@/db/fetchInChunks';
import { Invoice, VOIDED_INVOICE_STATUSES } from '@/db/models/Billing';
import { Customer } from '@/db/models/Customers';
import type { RegisterInvoiceInput } from './salesRegister';

export async function loadSalesRegister(
  storeId: string,
  from: Date,
  to: Date,
): Promise<RegisterInvoiceInput[]> {
  const invoices = await database.get<Invoice>('invoices').query(
    Q.where('store_id', storeId),
    Q.where('is_deleted', false),
    Q.where('status', Q.notIn(VOIDED_INVOICE_STATUSES)),
    Q.where('type', Q.oneOf(['SALE', 'CREDIT_NOTE'])),
    Q.where('created_at', Q.gte(from.getTime())),
    Q.where('created_at', Q.lt(to.getTime())),
  ).fetch();

  const customerIds = [...new Set(invoices.map((i) => i.customerId).filter((id): id is string => !!id))];
  const customers = await fetchInChunks(customerIds, (batch) =>
    database.get<Customer>('customers').query(Q.where('id', Q.oneOf(batch)), Q.where('is_deleted', false)).fetch(),
  );
  const custById = new Map(customers.map((c) => [c.id, c]));

  return invoices.map((i) => {
    const cust = i.customerId ? custById.get(i.customerId) : undefined;
    return {
      invoiceNumber: i.invoiceNumber,
      date: i.createdAt,
      customerName: cust?.name?.trim() || cust?.phone || 'Walk-in',
      gstin: cust?.gstin ?? null,
      type: i.type,
      taxable: i.taxableAmount,
      cgst: i.cgstAmount,
      sgst: i.sgstAmount,
      igst: i.igstAmount,
      cess: i.cessAmount,
      totalTax: i.totalTax,
      total: i.totalAmount,
      paymentStatus: i.paymentStatus,
    };
  });
}
